import { SocialLoginComponent } from './../social.login.component';
import { Component, EventEmitter, NgZone, Output } from '@angular/core';
import { ConfigService } from 'src/app/common/services/config/config.service';
import { CheckLoginStatusService } from '../checkLoginStatus.service';
import { PostMessageService } from '../post-message.service';

@Component({
  selector: 'ig-facebook-login',
  templateUrl: './facebook.login.component.html',
  styleUrls: ['./facebook.login.component.scss'],
})
export class FacebookLoginComponent {
  public static isUserLoggedIn: boolean;
  public static authResponse: any;
  public static userAccessToken: string;
  public static profilePicture: string;
  public static userFacebookId: string;

  @Output() public onLoginStatusChanged = new EventEmitter<boolean>();

  constructor(
    private checkLoginStatusService: CheckLoginStatusService,
    private postMessageService: PostMessageService,
    private ngZone: NgZone
  ) {
    if (!this.isNativeAppWebView() || !this.postMessageService.hasNativeBridge()) {
      this.whenFbSdkReady(() => {
        this.initFB();
        // Restore persisted session first so UI can update without waiting on FB cookies.
        FacebookLoginComponent.restoreFromStorage(this.isNativeAppWebView());
        this.validateFacebookSession();
      });
    }
  }

  private whenFbSdkReady(cb: () => void, attempt = 0) {
    if (window['FB']) {
      cb();
      return;
    }
    if (attempt > 40) {
      console.warn('Facebook SDK did not become available');
      return;
    }
    setTimeout(() => this.whenFbSdkReady(cb, attempt + 1), 500);
  }

  login() {
    // If in a WebView with a native bridge, delegate to the native app
    if (this.isNativeAppWebView() && this.postMessageService.hasNativeBridge()) {
      this.postMessageService.sendLoginRequest('facebook');
      return;
    }

    // Try FB SDK (works in browser and as WebView fallback when no bridge)
    if (!window['FB']) {
      // SDK not loaded — send postMessage as last resort (will loop back and trigger error)
      if (this.isNativeAppWebView()) {
        this.postMessageService.sendLoginRequest('facebook');
      }
      return;
    }

    window['FB'].login(
      (response: any) => {
        this.ngZone.run(() => {
          if (!response?.authResponse?.userID) {
            return;
          }

          this.applyAuthResponse(response.authResponse);
          this.sendLoginSuccessInfo();
        });
      },
      { scope: ConfigService.config?.facebook?.graphApi?.scope || 'email,public_profile' }
    );
  }

  // Send user info after successful login (always send if in WebView/iframe context)
  private sendLoginSuccessInfo() {
    // Send user info via postMessage if in WebView/iframe context
    if (this.postMessageService.isInWebView() && FacebookLoginComponent.isUserLoggedIn) {
      this.postMessageService.sendLoginSuccess({
        provider: 'facebook',
        userId: FacebookLoginComponent.userFacebookId,
        profilePicture: FacebookLoginComponent.profilePicture,
        accessToken: FacebookLoginComponent.userAccessToken,
      });
    }
  }

  public get isUserLoggedWithFB() {
    return FacebookLoginComponent.isUserLoggedIn;
  }

  public get profilePicture() {
    return FacebookLoginComponent.profilePicture;
  }

  public logout() {
    // If in a native app WebView, request app-level logout
    if (this.isNativeAppWebView()) {
      this.postMessageService.sendLogoutRequest('facebook');
      FacebookLoginComponent.clearLoginData();
      // Send logout success confirmation
      this.postMessageService.sendLogoutSuccess();
      return;
    }

    // Regular FB SDK logout for non-webview environments
    if (!window['FB']) {
      console.warn('Facebook SDK not loaded');
      FacebookLoginComponent.clearLoginData();
      return;
    }

    // FB.logout never calls back without an access token, so state can't depend on its callback.
    FacebookLoginComponent.clearLoginData();
    window['FB'].logout(() => {
      this.ngZone.run(() => {
        this.postMessageService.sendLogoutSuccess();
      });
    });
  }

  // Static method to update Facebook login status from app-level OAuth
  public static updateFromAppLogin(loginData: any) {
    if (loginData.isLoggedIn && loginData.provider === 'facebook') {
      FacebookLoginComponent.isUserLoggedIn = true;
      FacebookLoginComponent.userFacebookId = loginData.userId;
      FacebookLoginComponent.userAccessToken = loginData.accessToken;
      FacebookLoginComponent.profilePicture = loginData.profilePicture;
      FacebookLoginComponent.authResponse = {
        authResponse: {
          userID: loginData.userId,
          accessToken: loginData.accessToken,
        },
      };

      // Store in localStorage for compatibility with existing code
      localStorage.setItem(
        'social.login.facebookUser',
        JSON.stringify({
          userId: loginData.userId,
          profilePicture: loginData.profilePicture,
        })
      );

      SocialLoginComponent.loginStatus$.next(true);
    } else {
      FacebookLoginComponent.clearLoginData();
    }
  }

  // Static method to clear Facebook login data
  public static clearLoginData() {
    FacebookLoginComponent.isUserLoggedIn = false;
    FacebookLoginComponent.authResponse = null;
    FacebookLoginComponent.userAccessToken = null;
    FacebookLoginComponent.profilePicture = null;
    FacebookLoginComponent.userFacebookId = null;
    localStorage.removeItem('social.login.facebookUser');
    const currentProvider = SocialLoginComponent.appLoginData?.provider;
    if (!currentProvider || currentProvider === 'facebook') {
      SocialLoginComponent.isAppLevelLogin = false;
      SocialLoginComponent.appLoginData = null;
    }

    if (SocialLoginComponent.loginStatus$.getValue()) {
      SocialLoginComponent.loginStatus$.next(false);
    }
  }

  private applyAuthResponse(authResponse: { userID: string; accessToken?: string }) {
    if (!authResponse?.userID) {
      return;
    }

    const profilePicture = FacebookLoginComponent.buildProfilePicture(authResponse.userID);

    FacebookLoginComponent.isUserLoggedIn = true;
    FacebookLoginComponent.authResponse = { authResponse };
    FacebookLoginComponent.userFacebookId = authResponse.userID;
    FacebookLoginComponent.userAccessToken = authResponse.accessToken || null;
    FacebookLoginComponent.profilePicture = profilePicture;

    localStorage.setItem(
      'social.login.facebookUser',
      JSON.stringify({
        userId: authResponse.userID,
        profilePicture,
      })
    );

    this.checkLoginStatusService.applyFacebookUser({
      userId: authResponse.userID,
      profilePicture,
    });
    this.onLoginStatusChanged.emit(true);
  }

  public static buildProfilePicture(userId: string): string {
    const profilePictureTemplate =
      ConfigService.config?.facebook?.graphApi?.profilePicture;
    return profilePictureTemplate
      ? profilePictureTemplate.replace('${userId}', userId)
      : `https://graph.facebook.com/${userId}/picture?type=normal`;
  }

  public static resolveProfilePicture(userId: string): string {
    if (FacebookLoginComponent.userFacebookId === userId && FacebookLoginComponent.profilePicture) {
      return FacebookLoginComponent.profilePicture;
    }
    return FacebookLoginComponent.buildProfilePicture(userId);
  }

  private validateFacebookSession() {
    try {
      if (!window['FB']) {
        console.warn('Facebook SDK not available');
        return;
      }

      window['FB'].getLoginStatus(
        (response: {
          status: string;
          authResponse: { userID: string; accessToken: string };
        }) => {
          this.ngZone.run(() => {
            if (response.status === 'connected' && response.authResponse?.userID) {
              const hasStoredSession = !!localStorage.getItem('social.login.facebookUser');
              if (hasStoredSession || FacebookLoginComponent.isUserLoggedIn) {
                this.applyAuthResponse(response.authResponse);
                this.sendLoginSuccessInfo();
              }
              return;
            }

            if (response.status === 'not_authorized' && !this.isNativeAppWebView()) {
              FacebookLoginComponent.clearLoginData();
            }
            // status === 'unknown': leave persisted session alone
          });
        }
      );
    } catch (e) {
      console.warn('Facebook sync error:', e);
    }
  }

  private initFB() {
    // Only skip if in WebView with a working native bridge
    if (this.isNativeAppWebView() && this.postMessageService.hasNativeBridge()) {
      return;
    }

    // Singleton guard — index.html may also auto-init via the SDK URL hash.
    if ((window as any).__igFbInitialized) {
      return;
    }

    try {
      if (window['FB'] && ConfigService.config?.facebook?.sdkConfig) {
        window['FB'].init(ConfigService.config.facebook.sdkConfig);
      }
    } catch (e) {
      console.warn('Facebook init error:', e);
    } finally {
      (window as any).__igFbInitialized = true;
    }
  }

  // Method to check login status on component initialization
  public checkInitialLoginStatus() {
    FacebookLoginComponent.restoreFromStorage(this.isNativeAppWebView());
  }

  public static restoreFromStorage(isNativeWebView: boolean): void {
    const stored = localStorage.getItem('social.login.facebookUser');
    if (!stored) return;
    try {
      const userData = JSON.parse(stored);
      FacebookLoginComponent.isUserLoggedIn = true;
      FacebookLoginComponent.userFacebookId = userData.userId;
      FacebookLoginComponent.profilePicture = userData.profilePicture;

      if (isNativeWebView) {
        SocialLoginComponent.isAppLevelLogin = true;
        SocialLoginComponent.appLoginData = {
          ...userData,
          provider: 'facebook',
          isLoggedIn: true,
        };
      }

      SocialLoginComponent.loginStatus$.next(true);
    } catch (e) {
      console.warn('Error parsing stored Facebook user data:', e);
      localStorage.removeItem('social.login.facebookUser');
    }
  }
  private isNativeAppWebView(): boolean {
    const ua = navigator.userAgent || '';

    // Android WebView: "Android" + typically "; wv)" or "Version/x" without "Chrome/x"
    const isAndroid = /Android/i.test(ua);
    const hasWvToken = /\bwv\)/i.test(ua);
    const isAndroidWebView =
      isAndroid && (hasWvToken || (/Version\/\d+/i.test(ua) && !/Chrome\/\d+/i.test(ua)));

    // iOS WebView: iOS UA without "Safari" token (Safari / normal browsers include it)
    const isIOS = /iP(ad|hone|od)/i.test(ua);
    const isSafari =
      isIOS &&
      /Safari/i.test(ua) &&
      !/CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua);
    const isIOSWebView = isIOS && !isSafari;

    return isAndroidWebView || isIOSWebView;
  }
}
